import type { Segment, FetchByQueryResult } from "mobx-repository";
import { prop } from "ramda";
import type {
    PageInfo,
    PedestrianDevice,
    PedestrianDeviceCreate,
    PedestrianDevicesApi,
    PedestrianDeviceSortKey,
    PedestrianDeviceUpdate,
    RoutesPedestrianDevicesListPedestrianDevicesRequest,
} from "../../api";
import type { ApiResource } from "../../utils/elofleet-repository";
import { ElofleetRepository } from "../../utils/elofleet-repository";
import { runInAction } from "mobx";
import { defaultPageSize } from "../../utils/constants";
import type { UUID } from "../../utils/uuid";
import { Validation } from "../../utils/validation";

export type PedestrianDevicesQuery = Omit<
    RoutesPedestrianDevicesListPedestrianDevicesRequest,
    "page"
>;

export interface PedestrianDevicesApiResource extends ApiResource {
    entity: PedestrianDevice;
    query: PedestrianDevicesQuery;
    create: PedestrianDeviceCreate;
    sortKey: PedestrianDeviceSortKey;
}

export class RepositoryPedestrianDevices extends ElofleetRepository<PedestrianDevicesApiResource> {
    constructor(private readonly pedestrianDevicesApi: PedestrianDevicesApi) {
        super();
    }

    public validation = {
        create: new Validation((pedestrianDeviceCreate: PedestrianDeviceCreate) =>
            this.pedestrianDevicesApi.routesPedestrianDevicesValidateCreatePedestrianDevice({
                pedestrianDeviceCreate: pedestrianDeviceCreate,
            }),
        ),
        update: new Validation(
            (pedestrianDeviceUpdate: PedestrianDeviceUpdate, pedestrianDeviceId: UUID) =>
                this.pedestrianDevicesApi.routesPedestrianDevicesValidateUpdatePedestrianDevice({
                    pedestrianDeviceUpdate,
                    pedestrianDeviceId,
                }),
        ),
    };

    public fetchMetadata(query: PedestrianDevicesQuery): Promise<PageInfo> {
        return this.pedestrianDevicesApi.routesPedestrianDevicesListPedestrianDevicesMetadata(
            query,
        );
    }

    public async update(
        pedestrianDeviceId: UUID,
        pedestrianDeviceUpdate: PedestrianDeviceUpdate,
    ): Promise<PedestrianDevice> {
        const pedestrianDevice =
            await this.pedestrianDevicesApi.routesPedestrianDevicesUpdatePedestrianDevice({
                pedestrianDeviceUpdate,
                pedestrianDeviceId,
            });
        await this.waitForIdle();
        runInAction(() => {
            // We need to reset the entire repository because if we e.g.
            // changed the active status of a vehicle it should stop showing up
            // in all queries where it might have been cached already.
            this.reset();
            this.add(pedestrianDevice);
        });

        return pedestrianDevice;
    }

    public async create(pedestrianDeviceCreate: PedestrianDeviceCreate): Promise<PedestrianDevice> {
        const vehicle =
            await this.pedestrianDevicesApi.routesPedestrianDevicesCreatePedestrianDevice({
                pedestrianDeviceCreate,
            });
        await this.waitForIdle();
        runInAction(() => {
            // This reload is necessary to reload all queries that want to display "all" entities.
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(vehicle);
        });

        return vehicle;
    }

    public delete(): Promise<void> {
        throw new Error("Cannot delete pedestrianDevices: Not implemented.");
    }

    protected async fetchByQuery(
        query: PedestrianDevicesQuery,
        pagination: Segment,
    ): Promise<FetchByQueryResult<PedestrianDevice>> {
        const page = pagination.offset / this.defaultCount;
        const entities =
            await this.pedestrianDevicesApi.routesPedestrianDevicesListPedestrianDevices({
                ...query,
                page,
            });
        return { entities };
    }

    protected fetchById(pedestrianDeviceId: string): Promise<PedestrianDevice | undefined> {
        return this.pedestrianDevicesApi.routesPedestrianDevicesReadPedestrianDevice({
            pedestrianDeviceId,
        });
    }
    protected extractId = prop("id");
}
